import { generatePath, useNavigate, useParams } from "react-router";

import { MapPanel } from "~/modules/map/components/MapPanel";

import OverrideAutoValidationSection from "../../components/OverrideAutoValidationSection";
import { ProjectReviewVisitV2RejectButton } from "../../components/ProjectReviewVisitV2RejectButton";
import { useReviewVisitDecisionActions } from "../../hooks/useReviewVisitDecisionActions";
import { useProjectReviewVisitV2WeatherReportsRoute_GetData } from "./api/useProjectReviewVisitV2WeatherReportsRoute_GetData";
import { useProjectReviewVisitV2WeatherReportsRoute_OverrideAutoValidation } from "./api/useProjectReviewVisitV2WeatherReportsRoute_OverrideAutoValidation";
import { useProjectReviewVisitV2WeatherReportsRoute_UpdateConditions } from "./api/useProjectReviewVisitV2WeatherReportsRoute_UpdateConditions";
import WeatherReportContent from "./components/WeatherReportContent";
import WeatherReportEmptyState from "./components/WeatherReportEmptyState";
import { WeatherReportsHeader } from "./components/WeatherReportsHeader";
import { WeatherReportsPanelSkeleton } from "./components/WeatherReportsPanelSkeleton";
import { WeatherReportsProtocolSummary } from "./components/WeatherReportsProtocolSummary";
import { ProjectReviewVisitV2WeatherReportsRoute_GetDataDocument } from "./queries.generated";

export function ProjectReviewVisitV2WeatherReportsRoute() {
    const { clientSlug, projectSlug, visitId } = useParams();
    if (!clientSlug || !projectSlug || !visitId) throw new Error("invalid params");

    const navigate = useNavigate();

    const { visit, weatherReport, hasAllWeatherConditionsValid, isOverridden, loading } =
        useProjectReviewVisitV2WeatherReportsRoute_GetData(visitId);

    const overrideAutoValidation = useProjectReviewVisitV2WeatherReportsRoute_OverrideAutoValidation(visitId);

    const { handleReject, loading: decisionsLoading } = useReviewVisitDecisionActions({
        visitId,
        refetchQueries: [ProjectReviewVisitV2WeatherReportsRoute_GetDataDocument],
    });

    const { updateConditions } = useProjectReviewVisitV2WeatherReportsRoute_UpdateConditions();

    const backHref = generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId/", {
        clientSlug,
        projectSlug,
        visitId,
    });

    const handleOverrideClick = async (reason: string | null) => {
        await overrideAutoValidation.handleOverrideWeatherAutoValidation(reason);
        if (reason !== null) {
            navigate("../");
        }
    };

    if (loading) return <WeatherReportsPanelSkeleton backHref={backHref} />;

    return (
        visit && (
            <MapPanel>
                <WeatherReportsHeader backHref={backHref} />

                {visit?.protocolVisit && <WeatherReportsProtocolSummary protocolVisit={visit.protocolVisit} />}

                {weatherReport === null ? (
                    <WeatherReportEmptyState />
                ) : (
                    weatherReport !== undefined && (
                        <>
                            <WeatherReportContent
                                weatherReport={weatherReport}
                                visitStatus={visit.status}
                                onUpdate={async values => {
                                    await updateConditions(weatherReport.sourceReportId, values);
                                }}
                            />
                            {!hasAllWeatherConditionsValid && (
                                <OverrideAutoValidationSection
                                    isOverridden={isOverridden}
                                    overriddenReason={visit.weatherProtocolAutoValidationOverriddenReason}
                                    onOverride={reason => handleOverrideClick(reason)}
                                    onRevert={() => handleOverrideClick(null)}
                                    loading={overrideAutoValidation.loading}
                                    error={overrideAutoValidation.error}
                                />
                            )}
                        </>
                    )
                )}
                <ProjectReviewVisitV2RejectButton
                    visit={visit}
                    onReject={async reason => {
                        await handleReject(reason);
                        navigate(backHref);
                    }}
                    loading={decisionsLoading}
                />
            </MapPanel>
        )
    );
}
